/*!
=========================================================
* Regiform IMS Frontend - ErrorBoundary Component
=========================================================
* Component for catching and handling React errors
* Prevents UI freezing from unhandled errors
=========================================================
*/
import React from "react";
import { Result, Button } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log the error to an error reporting service here
    if (process.env.NODE_ENV === "production") {
      // Log to error reporting service
      console.error("Production error:", error);
    }
  }

  handleReload = () => {
    // Reset error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleReset = () => {
    // Just reset the error state without reloading
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={{ padding: "50px", textAlign: "center" }}>
          <Result
            icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
            title="Something went wrong"
            subTitle="An unexpected error occurred. Please try refreshing the page or contact support if the problem persists."
            extra={[
              <Button key="reset" onClick={this.handleReset}>
                Try Again
              </Button>,
              <Button 
                key="reload" 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>,
            ]}
          />
          
          {/* Show error details in development */}
          {process.env.NODE_ENV === "development" && (
            <details style={{ marginTop: "20px", textAlign: "left" }}>
              <summary style={{ cursor: "pointer", marginBottom: "10px" }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ 
                background: "#f5f5f5", 
                padding: "10px", 
                borderRadius: "4px",
                overflow: "auto",
                maxHeight: "300px"
              }}>
                {this.state.error && this.state.error.toString()}
                <br />
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
